import { useState } from 'react';
import { motion } from 'framer-motion';

const NotForEveryoneSection = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const voices = [
    {
      name: '田中さん',
      role: '移住3年目',
      background: '元IT企業勤務',
      image: 'https://public.readdy.ai/ai/img_res/56bf37eb905aadb8eeff3cf9fb2a0a6a.jpg',
      good: [
        '自然の中での暮らしは想像以上に心地よい',
        '人との距離感がちょうどいい。干渉しすぎず、でも困ったときは助けてくれる'
      ],
      hard: [
        '収入は正直、不安定。覚悟が必要',
        '車がないと生活できない。これは本当'
      ]
    },
    {
      name: '佐藤さん',
      role: '参加1年目',
      background: '建築未経験',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20woman%20in%20her%20late%2020s%20working%20on%20traditional%20house%20renovation%2C%20wearing%20work%20clothes%20and%20safety%20gear%2C%20determined%20expression%2C%20natural%20documentary%20photography%20style%2C%20rural%20Japan%20setting&width=400&height=400&seq=sato-voice-002&orientation=squarish',
      good: [
        '新しいスキルが身につく達成感',
        '「できた！」という瞬間の喜びは格別'
      ],
      hard: [
        '思った以上に時間がかかる。週末だけでは進まない',
        '専門知識の壁にぶつかることも多い'
      ]
    },
    {
      name: '山田さん',
      role: '地元農家',
      background: '農家3代目',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20elderly%20farmer%20in%20his%2060s%20standing%20in%20traditional%20Japanese%20rural%20farmland%2C%20wearing%20farming%20clothes%2C%20wise%20and%20welcoming%20expression%2C%20natural%20lighting%2C%20authentic%20documentary%20style%20photography%2C%20Yoshino%20mountains%20background&width=400&height=400&seq=yamada-voice-003&orientation=squarish',
      good: [
        '本気で取り組む姿勢を見せてくれる人',
        '長く続ける覚悟がある人'
      ],
      hard: [
        'すぐ辞めてしまわないか、正直心配',
        '観光気分で来られると、お互いに辛い'
      ]
    }
  ];

  const notSuitableFor = [
    {
      icon: 'ri-time-line',
      title: 'すぐに結果を求める人',
      description: '古民家リノベも、農業も、コミュニティづくりも、時間がかかります。「3ヶ月で形にしたい」という方には向いていません。'
    },
    {
      icon: 'ri-car-line',
      title: '車の運転ができない人',
      description: '吉野は公共交通機関が限られています。車がないと、活動への参加も日常生活も厳しいのが現実です。'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '安定収入を最優先する人',
      description: '地域での挑戦は、収入面で不安定になることも。「毎月決まった給料」を最優先する方には、おすすめできません。'
    },
    {
      icon: 'ri-user-line',
      title: '一人で完結したい人',
      description: 'コミュニティでの活動は、協力と対話が前提。「自分のペースで、誰にも邪魔されずに」という方には合わないかもしれません。'
    }
  ];

  return (
    <section className="py-20 bg-stone-50" aria-labelledby="not-for-everyone-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* リアルな声（正直版） */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 
              id="real-voices-heading" 
              className="text-3xl md:text-4xl font-bold text-stone-800 mb-4"
            >
              リアルな声（正直版）
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              良いことばかりじゃない。でも、それでも挑戦する価値がある。<br />
              参加者と地元の方の、本音を聞いてみてください。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {voices.map((voice, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div className="aspect-square w-full h-64 overflow-hidden">
                  <img
                    src={voice.image}
                    alt={`${voice.name}さんの写真 - ${voice.background}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-stone-800 mb-1">
                      {voice.name}
                    </h3>
                    <p className="text-sm text-amber-600 font-medium mb-1">
                      {voice.role}
                    </p>
                    <p className="text-xs text-stone-500">
                      {voice.background}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-2">
                        <i className="ri-check-line text-lg"></i>
                        {index === 2 ? '期待すること' : '良かったこと'}
                      </h4>
                      <ul className="space-y-2">
                        {voice.good.map((item, i) => (
                          <li key={i} className="text-sm text-stone-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-green-600 before:rounded-full">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-orange-700 mb-2 flex items-center gap-2">
                        <i className="ri-alert-line text-lg"></i>
                        {index === 2 ? '心配なこと' : '大変だったこと'}
                      </h4>
                      <ul className="space-y-2">
                        {voice.hard.map((item, i) => (
                          <li key={i} className="text-sm text-stone-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-orange-600 before:rounded-full">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 正直、こんな人には向いていません */}
        <div>
          <div className="text-center mb-12">
            <h2 
              id="not-for-everyone-heading" 
              className="text-3xl md:text-4xl font-bold text-stone-800 mb-4"
            >
              正直、こんな人には向いていません
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              ミスマッチを防ぐために、正直にお伝えします。<br />
              でも、「挑戦してみたい」という気持ちがあれば、まずは相談してください。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {notSuitableFor.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border-l-4 border-amber-500"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-2xl text-amber-700`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-stone-800 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* でも、挑戦したい気持ちがあれば... */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-amber-50 to-stone-50 rounded-2xl p-8 max-w-3xl mx-auto border border-amber-200">
              <p className="text-lg text-stone-700 mb-6 leading-relaxed">
                上記に当てはまっても、<strong className="text-amber-700">「それでも挑戦してみたい」</strong>という気持ちがあれば、<br />
                まずは相談してください。一緒に、あなたに合った関わり方を考えましょう。
              </p>

              <motion.a
                href="/consultation"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-600 text-white font-black px-8 py-4 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                aria-label="吉野なんでも相談所へ移動"
              >
                <i className="ri-chat-3-line text-xl w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                吉野なんでも相談所へ
              </motion.a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotForEveryoneSection;
